import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Ban, PencilLine } from "lucide-react";
import SummaryDialog from "./SummaryDialog";
import { shortLabel } from "./labels";

const Src = ({ s }) => <span className={`rounded px-1 text-[10px] font-semibold ${s === "riepilogo" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"}`}>{s === "riepilogo" ? "Riepilogo" : "Singola"}</span>;

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function RegistryPanel({ officeId, cross, onChanged }) {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [visits, setVisits] = useState(null);
  const [ops, setOps] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  const load = useCallback(async () => {
    try {
      const p = { date_from: from, date_to: to, office_id: officeId || undefined };
      const [v, o] = await Promise.all([api.get("/monitoring/visits", { params: p }), api.get("/monitoring/operations", { params: p })]);
      setVisits(v.data); setOps(o.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); setVisits([]); setOps([]); }
  }, [from, to, officeId]);
  useEffect(() => { load(); }, [load]);

  const voidOp = async (o) => {
    const reason = prompt("Motivo dell'annullamento:"); if (!reason) return;
    try { await api.patch(`/monitoring/operations/${o.id}`, { status: "ANNULLATA", reason }); toast.success("Operazione annullata"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };
  const voidVisit = async (v) => {
    const reason = prompt("Motivo dell'annullamento ingresso:"); if (!reason) return;
    try { await api.post(`/monitoring/visits/${v.id}/void`, { reason }); toast.success("Ingresso annullato"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <div className="space-y-3" data-testid="registry-panel">
      <div className="flex flex-wrap items-end gap-2 text-sm">
        <label className="block"><span className="text-xs text-slate-500">Dal</span><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="reg-from" /></label>
        <label className="block"><span className="text-xs text-slate-500">Al</span><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="reg-to" /></label>
        <span className="text-xs text-slate-500">{visits ? `${visits.filter((v) => !v.voided_at).length} ingressi · ${ops ? ops.filter((o) => o.status === "REGISTRATA").length : 0} operazioni` : ""}</span>
        <Button size="sm" className="ml-auto gap-1" onClick={() => setShowSummary(true)} data-testid="summary-edit-button"><PencilLine className="h-4 w-4" /> MODIFICA RIEPILOGO</Button>
      </div>
      {showSummary && <SummaryDialog cross={cross} officeId={officeId} onClose={() => setShowSummary(false)} onSaved={() => { load(); onChanged?.(); }} />}
      {visits === null || ops === null ? <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-brand" /></div> : (
        <div className="grid gap-3 lg:grid-cols-2">
          <Table title="Ingressi" testid="reg-visits" rows={visits} cols={["Ora", "Sede", "Operatore", "Origine", "Op. collegate", "Note", ""]}
            render={(v) => [fmt(v.occurred_at), v.office_name, v.operator_name || "—", <Src s={v.source} />, v.operations_count, v.voided_at ? <span className="text-danger">ANNULLATO: {v.void_reason}</span> : (v.notes || "—"),
              !v.voided_at && <Button variant="ghost" size="sm" className="h-6 px-1 text-danger" onClick={() => voidVisit(v)} data-testid={`void-visit-${v.id}`}><Ban className="h-3.5 w-3.5" /></Button>]} voided={(v) => !!v.voided_at} />
          <Table title="Operazioni" testid="reg-ops" rows={ops} cols={["Ora", "Sede", "Operatore", "Tipologia", "Origine", "Stato", ""]}
            render={(o) => [fmt(o.occurred_at), o.office_name, o.operator_name || "—", shortLabel({ name: o.type_name }), <Src s={o.source} />, o.status === "ANNULLATA" ? <span className="text-danger" title={o.void_reason}>ANNULLATA</span> : <span className="text-emerald-700">REGISTRATA</span>,
              o.status === "REGISTRATA" && <Button variant="ghost" size="sm" className="h-6 px-1 text-danger" onClick={() => voidOp(o)} data-testid={`void-op-${o.id}`}><Ban className="h-3.5 w-3.5" /></Button>]} voided={(o) => o.status === "ANNULLATA"} />
        </div>
      )}
      <p className="text-[11px] text-slate-400">Gli operatori possono annullare solo le proprie registrazioni di oggi e correggere il proprio riepilogo giornaliero entro il termine configurato dalla Direzione; Back Office e Direzione tutte le sedi. Ogni correzione è tracciata e lo storico non viene cancellato.</p>
    </div>
  );
}

function Table({ title, testid, rows, cols, render, voided }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <div className="border-b border-border bg-slate-50 px-2 py-1.5 text-xs font-semibold text-slate-600">{title} ({rows.length})</div>
      <table className="w-full text-xs" data-testid={testid}>
        <thead className="text-left text-slate-500"><tr>{cols.map((c, i) => <th key={i} className="px-2 py-1">{c}</th>)}</tr></thead>
        <tbody>{rows.length === 0 ? <tr><td colSpan={cols.length} className="px-2 py-4 text-center text-slate-400">Nessuna registrazione</td></tr>
          : rows.map((r) => <tr key={r.id} className={`border-t border-slate-100 ${voided(r) ? "opacity-60" : ""}`} data-testid={`${testid}-row-${r.id}`}>{render(r).map((c, i) => <td key={i} className="px-2 py-1">{c}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}
